import { useState, useEffect } from 'react';
import i18n from '../i18n/i18n';
import { useTheme } from '../hooks/useTheme';

// Components
import HeaderBar from '../components/HeaderBar';
import ContentInfo from '../components/ContentInfo';
import SignEvalButton from '../components/SignEvalButton';
import DeleteSession from '../components/Modals/DeleteSession';
import SessionSkeleton from '../components/Skeleton/SessionSkeleton';
import QRCode from '../components/Drawer/DrawerQRCode';
import SlotPicker from '../components/Slots/SlotPicker';
import DrawerEditSession from '../components/Drawer/DrawerEditSession';

// MUI imports
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import GestureOutlinedIcon from '@mui/icons-material/GestureOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// Helpers
import { useSessionStore } from '../store/SessionStore';
import { useUserStore } from '../store/UserStore';
import { getSlotsBySessionId } from '../helpers/api/callSlot';
import { formatSlotForSlotPicker } from '../helpers/functions/formatSlot';
import { SlotData, SlotDayData } from '../types/slotData';
import { InternData } from '../types/internData';
import { getSignature } from '../helpers/api/callSignature';
import { closeSession, openSession } from '../helpers/api/callSession';

const Session = () => {
    const [slotsDay, setSlotsDay] = useState<SlotDayData[]>([]);
    const [selectedSlot, setSelectedSlot] = useState<SlotData>();
    const [isLoadingSlots, setIsLoadingSlots] = useState(true);
    const [isLoadingClosing, setIsLoadingClosing] = useState(false);

    const theme = useTheme();
    const locale = i18n.language;
    const { session, setSession, loading } = useSessionStore();
    const { user } = useUserStore();
    const [signee, setSignee] = useState<InternData>({
        id: user.id || '',
        civility: user.civility || '',
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        email: user.email || '',
        societyId: user.societyId || '',
        registrationNumber: user.registrationNumber || '',
        hasSigned: false,
        isPresent: true,
        date: ''
    });

    const linkSession = process.env.REACT_APP_QW_URL + '/evaluation/' + session.sessionToken;
    const linkEvalTrainer = process.env.REACT_APP_QW_URL + '/answer/formateur/' + session.id + '/evaluation/';

    useEffect(() => {
        if (!loading) {
            const fetchSlots = async () => {
                setIsLoadingSlots(true);
                const slots = await getSlotsBySessionId(session.id);
                slots && setSlotsDay(formatSlotForSlotPicker(slots, locale));
                setIsLoadingSlots(false);
            };

            fetchSlots();
        }
    }, [loading]);

    useEffect(() => {
        const fetchTrainerSignature = async () => {
            if (selectedSlot && user?.id) {
                try {
                    const signature = await getSignature(selectedSlot.id, user.id);

                    if (signature) {
                        setSignee((prev) => ({
                            ...prev,
                            hasSigned: true,
                            date: signature.date
                        }));
                    } else {
                        setSignee((prev) => ({
                            ...prev,
                            hasSigned: false,
                            date: ''
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching trainer signature:', error);
                }
            }
        };

        fetchTrainerSignature();
    }, [selectedSlot]);

    const handleCloseSession = async () => {
        try {
            setIsLoadingClosing(true);
            await closeSession(session.id);
            setSession({ ...session, closingDate: new Date().toISOString() });
        } catch (error) {
            console.error('Error closing session:', error);
        } finally {
            setIsLoadingClosing(false);
        }
    };

    const handleOpenSession = async () => {
        try {
            setIsLoadingClosing(true);
            await openSession(session.id);
            setSession({ ...session, closingDate: null });
        } catch (error) {
            console.error('Error opening session:', error);
        } finally {
            setIsLoadingClosing(false);
        }
    };

    if (loading || isLoadingSlots) {
        return <SessionSkeleton />;
    }

    return (
        <Box sx={{ width: '100%', height: '100vh', alignItems: 'center', backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
            <HeaderBar />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '0 auto',
                    width: '100%',
                    height: '92.5%'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '92.5%',
                        margin: '0 auto'
                    }}
                >
                    <ContentInfo margin="0.5em 0 0.3em 0" padding="0.5em 0">
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '92.5%',
                                minHeight: '13vh'
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '80%', height: '100%' }}>
                                <Typography sx={{ padding: '0.7em 0', fontSize: '1.15em', color: theme.palette.secondary.main }}>{session.trainingLabel}</Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-evenly',
                                        width: 'auto',
                                        color: theme.palette.text.primary
                                    }}
                                >
                                    <Typography sx={{ fontSize: '0.85em' }}>{session.sessionName}</Typography>
                                    <Typography sx={{ fontSize: '0.85em' }}>{`${session.sessionBegin} - ${session.sessionEnd}`}</Typography>
                                    <Typography sx={{ fontSize: '0.85em' }}>{session.trainerName}</Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '18%',
                                    margin: '0 0.2em',
                                    height: '100%'
                                }}
                            >
                                <QRCode link={linkSession} token={session.sessionToken} />
                                <Typography sx={{ fontWeight: '600', color: theme.palette.text.custom }}>{session.sessionToken}</Typography>
                            </Box>
                        </Box>
                    </ContentInfo>
                    {slotsDay.length > 0 && <SlotPicker slotList={slotsDay} onSlotChange={setSelectedSlot} editMode={false} setSlotsDay={(slot) => setSlotsDay((prev) => [...prev, slot])} />}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', width: '100%', height: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            width: '92.5%',
                            height: '45%',
                            maxHeight: '24vh',
                            marginTop: '0.25em'
                        }}
                    >
                        <SignEvalButton
                            route={`/sessions/${session.id}/interns`}
                            linkProps={{ selectedSlot }}
                            variant="contained"
                            size="large"
                            backgroundColor="blue"
                            startIcon={<PeopleAltOutlinedIcon />}
                        >
                            <Typography sx={{ color: theme.palette.text.secondary }}>{i18n.t('Session.actions.trainees')}</Typography>
                            {session.trainees.length}
                        </SignEvalButton>
                        <SignEvalButton
                            route="/sign"
                            linkProps={{ signee, selectedSlot }}
                            variant="contained"
                            size="large"
                            backgroundColor={signee.hasSigned ? 'green' : 'blue'}
                            startIcon={<GestureOutlinedIcon />}
                        >
                            <Typography sx={{ color: theme.palette.text.secondary }}>{i18n.t('Session.actions.signature')}</Typography>
                            &nbsp;
                        </SignEvalButton>
                        <a
                            href={linkEvalTrainer}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', textDecoration: 'none', width: '100%' }}
                        >
                            <SignEvalButton variant="contained" size="large" backgroundColor="blue" startIcon={<InventoryOutlinedIcon />}>
                                <Typography sx={{ color: theme.palette.text.secondary }}>{i18n.t('Session.actions.evaluation')}</Typography>
                                &nbsp;
                            </SignEvalButton>
                        </a>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '92.5%',
                            marginBottom: '3em',
                            gap: '1.25em'
                        }}
                    >
                        <DrawerEditSession session={session} slots={slotsDay} onSlotChange={setSelectedSlot} />
                        <DeleteSession sessionId={session.id} sessionName={session.trainingLabel} />
                        <IconButton
                            sx={{
                                width: '2.5em',
                                height: '1.75em',
                                borderRadius: '10px',
                                backgroundColor: theme.palette.closed.main,
                                color: theme.palette.text.secondary
                            }}
                            onClick={session.closingDate ? handleOpenSession : handleCloseSession}
                        >
                            {isLoadingClosing ? <CircularProgress size={20} sx={{ color: theme.palette.text.secondary }} /> : session.closingDate ? <LockOpenIcon /> : <LockOutlinedIcon />}
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Session;
